import { cellAt } from './board.js';
import { floodRegion } from './floodfill.js';
import { getControlledRegion, isCornerConnected } from './region.js';
import type { Board } from './types.js';
import { coordKey } from './types.js';

const MAX_NODES = 20_000;
const MAX_DEPTH = 40;

const boardKey = (b: Board): string => b.cells.map((r) => r.map((c) => c.color).join('')).join('|');

const candidateColors = (b: Board): string[] => {
  const region = getControlledRegion(b, b.start);
  const cur = cellAt(b, b.start).color;
  const adjacent = new Set<string>();
  region.forEach((k) => {
    const [xs, ys] = k.split(',');
    const x = Number(xs);
    const y = Number(ys);
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const nx = x + dx;
      const ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= b.size || ny >= b.size) continue;
      if (region.has(coordKey({ x: nx, y: ny }))) continue;
      const cell = b.cells[ny]?.[nx];
      if (!cell || cell.tile === 'locked') continue;
      if (cell.color !== cur) adjacent.add(cell.color);
    }
  });
  return [...adjacent];
};

/**
 * BFS over color-pick moves to find the minimum number of floods
 * that connect start → end. Returns null if no solution within limits.
 */
export const minStepsToWin = (root: Board): number | null => {
  if (isCornerConnected(root)) return 0;
  const seen = new Set<string>([boardKey(root)]);
  let frontier: Board[] = [root];
  for (let depth = 1; depth <= MAX_DEPTH; depth++) {
    const next: Board[] = [];
    let visited = 0;
    for (const b of frontier) {
      for (const color of candidateColors(b)) {
        const { nextCells } = floodRegion(b, color);
        const nb: Board = { ...b, cells: nextCells };
        const k = boardKey(nb);
        if (seen.has(k)) continue;
        seen.add(k);
        if (isCornerConnected(nb)) return depth;
        next.push(nb);
        visited++;
        if (seen.size > MAX_NODES) return null;
      }
    }
    if (next.length === 0) return null;
    frontier = next;
    if (visited === 0) return null;
  }
  return null;
};
